import { QueryStatus, CustomAxiosErrorType, CustomMailFolderType } from '@ukpostbox/client-data';
import {
	AlertBanner,
	ButtonSubmit,
	Colour,
	ColourStateSolid,
	ContentBlock,
	ContentLoader,
	FlexGap,
	FormWrapperLabel,
	Modal,
	ModalType,
	StackVertical,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect } from 'react';
import { CustomMailFoldersType } from '../../Types/MailboxProviderTypes';

interface CreateCustomFolderModalType extends ModalType {
	customFolderStatus: QueryStatus;
	customFolderData?: Array<CustomMailFolderType>;
	customFolderError: CustomAxiosErrorType | null;

	activeCustomFolderData: CustomMailFoldersType | null;

	CreateCustomFolderCallback: (
		newFolderData: CustomMailFolderType,
		SuccessCallback?: (newFolderId: number) => void,
	) => void;
	EditCustomFolderCallback: (editFolderData: CustomMailFolderType, SuccessCallback?: () => void) => void;
}

function CreateCustomFolderModal(props: CreateCustomFolderModalType) {
	const { t } = useTranslation();

	const { control, handleSubmit, reset, formState, setValue } = useForm({
		defaultValues: {
			id: props.activeCustomFolderData?.id || '',
			description: props.activeCustomFolderData?.description || '',
			colour: props.activeCustomFolderData?.colour || Colour.Secondary,
			parentFolderId: props.activeCustomFolderData?.parentFolderId || null,
		},
		mode: 'onChange',
	});

	const UpdateInitialData = () => {
		setValue('id', props.activeCustomFolderData?.id || '');
		setValue('description', props.activeCustomFolderData?.description || '');
		setValue('colour', props.activeCustomFolderData?.colour as string);
		setValue('parentFolderId', props.activeCustomFolderData?.parentFolderId || null);
	};

	const AddFolder = (newFolder: CustomMailFolderType) => {
		props.CreateCustomFolderCallback(newFolder);
		reset();
	};

	const EditFolder = (editFolder: CustomMailFolderType) => {
		props.EditCustomFolderCallback(editFolder);
		reset();
	};

	useEffect(() => {
		reset();
		if (props.activeCustomFolderData) {
			UpdateInitialData();
		}
	}, [props.isOpen, props.activeCustomFolderData]);

	return (
		<Modal
			title={
				props.activeCustomFolderData ? t('customFolders.editFolder.title') : t('customFolders.newFolder.title')
			}
			isOpen={props.isOpen}
			closeCallback={() => props.closeCallback()}
		>
			<form
				onSubmit={handleSubmit((data: any) =>
					props.activeCustomFolderData ? EditFolder(data) : AddFolder(data),
				)}
			>
				<fieldset className='flex-column flex-gap-md'>
					<Controller
						control={control}
						name='description'
						rules={{ required: 'This is a required field', minLength: { value: 2, message: 'Too short' } }}
						render={({ field, fieldState }) => (
							<FormWrapperLabel
								label={t('customFolders.newFolder.labels.name')}
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<input
									type='text'
									defaultValue={field.value}
									aria-invalid={fieldState.invalid ? 'true' : 'false'}
									autoComplete='off'
									autoFocus
									onChange={(e) => field.onChange(e.currentTarget.value)}
								/>
							</FormWrapperLabel>
						)}
					/>

					<Controller
						control={control}
						name='colour'
						rules={{ required: 'This is a required field' }}
						render={({ field }) => (
							<FormWrapperLabel label={t('customFolders.newFolder.labels.colour')} hideValidationMessage>
								<div className='flex-row flex-gap-md'>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<div
										className={`colour-block ${Colour.Secondary} ${field.value === ColourStateSolid.Secondary ? 'colour-selected' : ''}`}
										onClick={() => field.onChange(ColourStateSolid.Secondary)}
										onKeyDown={(e) =>
											e.key === 'Enter' ? field.onChange(ColourStateSolid.Secondary) : undefined
										}
										role='button'
										tabIndex={0}
									/>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<div
										className={`colour-block ${Colour.Primary} ${field.value === ColourStateSolid.Primary ? 'colour-selected' : ''}`}
										onClick={() => field.onChange(ColourStateSolid.Primary)}
										onKeyDown={(e) =>
											e.key === 'Enter' ? field.onChange(ColourStateSolid.Primary) : undefined
										}
										role='button'
										tabIndex={0}
									/>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<div
										className={`colour-block ${Colour.Warning} ${field.value === ColourStateSolid.Warning ? 'colour-selected' : ''}`}
										onClick={() => field.onChange(ColourStateSolid.Warning)}
										onKeyDown={(e) =>
											e.key === 'Enter' ? field.onChange(ColourStateSolid.Warning) : undefined
										}
										role='button'
										tabIndex={0}
									/>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<div
										className={`colour-block ${Colour.Alert} ${field.value === ColourStateSolid.Alert ? 'colour-selected' : ''}`}
										onClick={() => field.onChange(ColourStateSolid.Alert)}
										onKeyDown={(e) =>
											e.key === 'Enter' ? field.onChange(ColourStateSolid.Alert) : undefined
										}
										role='button'
										tabIndex={0}
									/>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<div
										className={`colour-block ${Colour.Success} ${field.value === ColourStateSolid.Success ? 'colour-selected' : ''}`}
										onClick={() => field.onChange(ColourStateSolid.Success)}
										onKeyDown={(e) =>
											e.key === 'Enter' ? field.onChange(ColourStateSolid.Success) : undefined
										}
										role='button'
										tabIndex={0}
									/>
								</div>
							</FormWrapperLabel>
						)}
					/>

					{props.customFolderStatus === 'pending' ? (
						<ContentLoader />
					) : props.customFolderStatus === 'error' ? (
						<ContentBlock
							error
							title={props.customFolderError?.name as string}
							content={props.customFolderError?.message}
						/>
					) : (
						<>
							{props.activeCustomFolderData?.childFolders && (
								<StackVertical gap={FlexGap.ExtraSmall}>
									<label>{t('customFolders.newFolder.labels.hasParent')}</label>
									<AlertBanner
										title='Folders containing nested folders can not have their own parent'
										colour={Colour.Warning}
									/>
								</StackVertical>
							)}

							{!props.activeCustomFolderData?.childFolders &&
								props.customFolderData &&
								props.customFolderData.length > 0 && (
									<Controller
										control={control}
										name='parentFolderId'
										render={({ field, fieldState }) => (
											<FormWrapperLabel
												label={t('customFolders.newFolder.labels.hasParent')}
												invalid={fieldState.invalid}
												validationMessage={fieldState.error?.message}
											>
												<select
													name='parentFolderId'
													defaultValue={`${field.value}`}
													aria-invalid={fieldState.invalid ? 'true' : 'false'}
													disabled={props.activeCustomFolderData?.childFolders}
													onChange={(e) => {
														field.onChange(
															e.currentTarget.value === '-1'
																? null
																: parseInt(e.currentTarget.value, 10),
														);
													}}
												>
													{props.customFolderData?.map((folder) =>
														!folder.parentFolderId &&
														!props.activeCustomFolderData?.childFolders &&
														props.activeCustomFolderData?.id !== folder.id ? (
															<option
																key={`newFolderOption${folder.id}`}
																value={folder.id}
															>
																{folder.description}
															</option>
														) : null,
													)}
												</select>
											</FormWrapperLabel>
										)}
									/>
								)}
						</>
					)}

					<ButtonSubmit
						title={props.activeCustomFolderData ? t('actions.update') : t('actions.save')}
						fullWidth
						colour={ColourStateSolid.Primary}
						disabled={!formState.isValid}
					/>
				</fieldset>
			</form>
		</Modal>
	);
}

export default CreateCustomFolderModal;
